import React, { useContext, useEffect, useState } from 'react';
import { Card, CardBody, Col } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { OfferService } from './service';
import { JobService } from '../job/service';
import { UserService } from '../user/service';
import { ErrorToast, SuccessToast } from '../../shared/toasters/toasters';
import DeleteModal from '../../Common/DeleteModal';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { EnumsContext } from '../../context/EnumsContext';
import { formatDateToYYYYMMDDLocal } from '../../shared/utils/functions';
import { useTranslation } from 'react-i18next';
import Pagination from '../../Common/Pagination';

const OfferList = () => {
  const { t } = useTranslation(); // Initialize translation hook
  const [list, setList] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [term, setTerm] = useState('');
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [offerToDelete, setOfferToDelete] = useState<number | null>(null);
  const [jobList, setJobList] = useState<any[]>([]);
  const [userList, setUserList] = useState<any[]>([]);
  const [selectedJob, setSelectedJob] = useState<number | null>(null);
  const [selectedUser, setSelectedUser] = useState<number | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const navigate = useNavigate();
  const [statusOptions, setStatusOptions] = useState<any[]>([]);
  const enumsContext = useContext(EnumsContext);

  const entriesPerPageOptions = [
    { label: '5', value: 5 },
    { label: '10', value: 10 },
    { label: '20', value: 20 },
    { label: '30', value: 30 },
    { label: '40', value: 40 },
  ];

  const handleTermChange = (e: any) => {
    setTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleEntriesPerPageChange = (selectedOption: any) => {
    setEntriesPerPage(selectedOption ? selectedOption.value : 10);
    setCurrentPage(1);
  };

  const handleJobChange = (selectedOption: any) => {
    setSelectedJob(selectedOption ? selectedOption.value : null);
    setCurrentPage(1);
  };

  const handleUserChange = (selectedOption: any) => {
    setSelectedUser(selectedOption ? selectedOption.value : null);
    setCurrentPage(1);
  };

  const handleStatusChange = (selectedOption: any) => {
    setSelectedStatus(selectedOption ? selectedOption.value : null);
    setCurrentPage(1);
  };

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleDeleteClick = (id: number) => {
    setOfferToDelete(id);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (offerToDelete !== null) {
      try {
        await OfferService.delete(offerToDelete);
        SuccessToast(t('offer.successDelete'));
        setShowDeleteModal(false);
        setOfferToDelete(null);
        fetchData();
      } catch (error) {
        ErrorToast(error);
      }
    }
  };

  const fetchData = async () => {
    try {
      const response = await OfferService.getAll({
        page: currentPage,
        perPage: entriesPerPage,
        term,
        jobId: selectedJob,
        userId: selectedUser,
        status: selectedStatus,
        fromDate: fromDate ? formatDateToYYYYMMDDLocal(fromDate) : null,
        toDate: toDate ? formatDateToYYYYMMDDLocal(toDate) : null,
      });
      const data = response.data;
      setList(data);
      setTotalPages(response.totalPages);
    } catch (error) {
      ErrorToast(error);
    }
  };

  const fetchJobs = async () => {
    try {
      const response = await JobService.getAll({ perPage: 0, page: 1 });
      const jobs = response.data.map((job: any) => ({
        label: job.title,
        value: job.id,
      }));
      setJobList(jobs);
    } catch (error) {
      ErrorToast(error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await UserService.getAll({ perPage: 0, page: 1 });
      const users = response.data.map((user: any) => ({
        label: `${user.firstName} ${user.lastName}`,
        value: user.id,
      }));
      setUserList(users);
    } catch (error) {
      ErrorToast(error);
    }
  };

  useEffect(() => {
    fetchJobs();
    fetchUsers();
  }, []);

  useEffect(() => {
    if (enumsContext?.enums?.offerStatus) {
      const options = enumsContext.enums.offerStatus.map((val: any) => ({
        label: val[0].toUpperCase() + val.slice(1),
        value: val,
      }));
      setStatusOptions(options);
    }
  }, [enumsContext?.enums?.offerStatus]);

  useEffect(() => {
    fetchData();
  }, [currentPage, entriesPerPage, term, selectedJob, selectedUser, selectedStatus, fromDate, toDate]);

  return (
    <React.Fragment>
      <Col>
        <Card>
          <CardBody className="card-body">
            <div className="text-end">
              <button
                className="btn btn-primary text-end"
                onClick={() => navigate('/offers/create')}
              >
                <i className="ti ti-plus f-18"></i> {t('offer.add')}
              </button>
            </div>

            <div className="d-sm-flex align-items-center mb-3">
              <ul className="list-inline me-auto my-1">
                <li className="list-inline-item">
                  <Select
                    placeholder={t('offer.entriesPerPage')}
                    options={entriesPerPageOptions}
                    onChange={handleEntriesPerPageChange}
                    defaultValue={entriesPerPageOptions.find(
                      (option) => option.value === entriesPerPage
                    )}
                    classNamePrefix="react-select"
                  />
                </li>
              </ul>
              <ul className="list-inline ms-auto my-1 mt-3 d-flex">
                <li className="list-inline-item">
                  <Select
                    placeholder={t('offer.selectJob')}
                    options={jobList}
                    onChange={handleJobChange}
                    isClearable
                    classNamePrefix="react-select"
                  />
                </li>
                <li className="list-inline-item">
                  <Select
                    placeholder={t('offer.selectUser')}
                    options={userList}
                    onChange={handleUserChange}
                    isClearable
                    classNamePrefix="react-select"
                  />
                </li>
                <li className="list-inline-item">
                  <Select
                    placeholder={t('offer.selectStatus')}
                    options={statusOptions}
                    onChange={handleStatusChange}
                    isClearable
                    classNamePrefix="react-select"
                  />
                </li>
                <li className="list-inline-item">
                  <DatePicker
                    selected={fromDate}
                    onChange={(date: Date | null) => setFromDate(date)}
                    placeholderText={t('offer.fromDate')}
                    className="form-control"
                    dateFormat="dd.MM.yyyy"
                  />
                </li>
                <li className="list-inline-item">
                  <DatePicker
                    selected={toDate}
                    onChange={(date: Date | null) => setToDate(date)}
                    placeholderText={t('offer.toDate')}
                    className="form-control"
                    dateFormat="dd.MM.yyyy"
                  />
                </li>
                <li className="list-inline-item">
                  <form className="">
                    <input
                      type="search"
                      className="form-control"
                      placeholder={t('offer.searchPlaceholder')}
                      onChange={handleTermChange}
                    />
                  </form>
                </li>
              </ul>
            </div>
            <div className="table-responsive">
              <table className="table table-hover sticky-actions" id="pc-dt-simple-1">
                <thead>
                  <tr>
                    <th>{t('offer.id')}</th>
                    <th>{t('offer.job')}</th>
                    <th>{t('offer.user')}</th>
                    <th>{t('offer.status')}</th>
                    <th>{t('offer.createdAt')}</th>
                    <th>{t('offer.updatedAt')}</th>
                    <th className="text-end sticky-col">{t('offer.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((item: any, index: number) => (
                    <tr key={index}>
                      <td>{item.id}</td>
                      <td className="wrap-col">{item.job?.title || 'N/A'}</td>
                      <td>
                        {item.user?.firstName
                          ? `${item.user.firstName} ${item.user.lastName}`
                          : 'N/A'}
                      </td>
                      <td>{item.status}</td>
                      <td>{new Date(item.createdAt).toLocaleDateString()}</td>
                      <td>{new Date(item.updatedAt).toLocaleDateString()}</td>
                      <td className="text-end text-nowrap sticky-col">
                        <ul className="list-inline mb-0">
                          <li
                            className="list-inline-item"
                            onClick={() => navigate(`/offers/edit/${item.id}`)}
                          >
                            <a
                              href="#"
                              className="avtar avtar-s btn-link-success btn-pc-default"
                            >
                              <i className="ti ti-edit f-20"></i>
                            </a>
                          </li>
                          <li className="list-inline-item">
                            <span
                              role="button"
                              className="avtar avtar-s btn-link-danger btn-pc-default"
                              onClick={() => handleDeleteClick(item.id)}
                            >
                              <i className="ti ti-trash f-20"></i>
                            </span>
                          </li>
                        </ul>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              previousLabel={t('offer.previous')}
              nextLabel={t('offer.next')}
            />
          </CardBody>
        </Card>
      </Col>

      <DeleteModal
        show={showDeleteModal}
        handleClose={() => setShowDeleteModal(false)}
        handleDeleteId={handleDeleteConfirm}
        name={t('offer.name')}
      />
    </React.Fragment>
  );
};

export default OfferList;